/**
 * Brand-voice sanitizer for the free-text `additional_properties.notes` field.
 *
 * The notes stem from a data import (#470) and use an ALL-CAPS toxicity prefix
 * ("GIFTIG:", "LEICHT GIFTIG:", "STARK GIFTIG:", "HOCHGIFTIG:") plus occasional
 * exclamation marks — both violate the brand guideline ("ruhig, kompetent, keine
 * Ausrufezeichen oder Superlative"). The toxicity facts themselves are already
 * surfaced brand-clean via ToxicWarningBadge/DataDisclaimerBadge, so here we only
 * soften the raw text: known CAPS toxicity words → sentence case, "!" → ".".
 *
 * The CAPS vocabulary in the data is a closed 5-word set (GIFTIG / LEICHT / STARK /
 * HOCHGIFTIG — always as the leading toxicity prefix — plus a single legitimate
 * "PKW"), so this targeted mapping is exhaustive and leaves genuine acronyms
 * untouched. LEICHT and STARK only ever appear paired with GIFTIG (verified across
 * all 608 rows), so the phrase rules below cover every occurrence.
 *
 * The notes also use a single "|" as an import delimiter between the toxicity
 * sentence and the general cultivation note (213/608 rows, always exactly one).
 * It carries no meaning for the reader, so we turn it into a normal sentence
 * boundary.
 */

// Order matters: multi-word phrases before the standalone "GIFTIG" rule.
const TOXICITY_CAPS_REPLACEMENTS = [
  [/\bLEICHT GIFTIG\b/g, 'Leicht giftig'],
  [/\bSTARK GIFTIG\b/g, 'Stark giftig'],
  [/\bHOCHGIFTIG\b/g, 'Hochgiftig'],
  [/\bGIFTIG\b/g, 'Giftig'],
];

/**
 * Returns a brand-voice-conformant version of a plant note: softens the ALL-CAPS
 * toxicity prefix to sentence case and replaces exclamation marks with periods.
 *
 * @param {*} text - Raw note text (any type; non-strings yield '')
 * @returns {string} Sanitized note, or '' when there is nothing to show
 */
export function sanitizePlantNote(text) {
  if (!text || typeof text !== 'string') return '';

  let out = text;
  for (const [pattern, replacement] of TOXICITY_CAPS_REPLACEMENTS) {
    out = out.replace(pattern, replacement);
  }

  // Exclamation marks (Schreckens-Wording) → neutral period; collapse runs and
  // drop any whitespace directly before the mark ("essbar !" → "essbar.").
  out = out.replace(/\s*!+/g, '.');

  // Import delimiter "|" → sentence boundary. Use a plain space when the left
  // side already ends in sentence punctuation (avoids ".. "), else a period.
  out = out.replace(/\s*\|\s*/g, (_match, offset, str) => {
    const prevChar = str[offset - 1] || '';
    return /[.!?;:]/.test(prevChar) ? ' ' : '. ';
  });

  return out.trim();
}
